import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Stepper, Step, StepLabel, Button, Grid, Chip, List, ListItem, ListItemText, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useLanguage } from '../context/LanguageContext';

const initialOrders = [
    { id: '#D-1024', table: 'Table 4', time: '10 mins ago', statusStep: 2, items: 'Butter Chicken x1, Butter Naan x3' },
    { id: '#D-1025', table: 'Table 9', time: '5 mins ago', statusStep: 1, items: 'Paneer Tikka x1, Garlic Naan x2' },
    { id: '#D-1026', table: 'Table 2', time: 'Just Now', statusStep: 0, items: 'Masala Papad x2, Veg Kolhapuri x1' }
];

export default function LiveStatusView() {
    const { t, currentLanguage } = useLanguage();
    const [orders, setOrders] = useState(initialOrders);
    const [selectedOrderId, setSelectedOrderId] = useState('#D-1024');

    const steps = [
        currentLanguage === 'mr' ? 'ऑर्डर प्राप्त' : currentLanguage === 'hi' ? 'ऑर्डर प्राप्त' : 'Order Received',
        currentLanguage === 'mr' ? 'किचनमध्ये तयार होत आहे' : currentLanguage === 'hi' ? 'रसोई में तैयार' : 'Preparing in Kitchen',
        currentLanguage === 'mr' ? 'वाढण्यासाठी तयार' : currentLanguage === 'hi' ? 'परोसने के लिए तैयार' : 'Ready to Serve',
        currentLanguage === 'mr' ? 'वाढले गेले' : currentLanguage === 'hi' ? 'परोस दिया गया' : 'Served'
    ];

    const getStepIcon = (index) => {
        switch (index) {
            case 0: return <ReceiptLongIcon sx={{ color: 'primary.main' }} />;
            case 1: return <SoupKitchenIcon sx={{ color: 'warning.main' }} />;
            case 2: return <DeliveryDiningIcon sx={{ color: 'success.main' }} />;
            case 3: return <CheckCircleIcon sx={{ color: 'secondary.main' }} />;
            default: return null;
        }
    };

    const handleNextStep = (orderId) => {
        setOrders(prev => prev.map(order => {
            if (order.id === orderId && order.statusStep < 3) {
                return { ...order, statusStep: order.statusStep + 1 };
            }
            return order;
        }));
    };

    const handlePrevStep = (orderId) => {
        setOrders(prev => prev.map(order => {
            if (order.id === orderId && order.statusStep > 0) {
                return { ...order, statusStep: order.statusStep - 1 };
            }
            return order;
        }));
    };

    const selectedOrder = orders.find(o => o.id === selectedOrderId) || orders[0];

    return (
        <Box sx={{ p: 4, maxWidth: 1200, margin: '0 auto' }}>
            <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, color: '#008751' }}>
                {currentLanguage === 'mr' ? 'लाईव्ह ऑर्डर ट्रॅकर' : currentLanguage === 'hi' ? 'लाइव ऑर्डर ट्रैकर' : 'Live Order Status Tracker'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#475569', mb: 4 }}>
                {currentLanguage === 'mr' 
                    ? 'प्रत्येक ऑर्डरची सद्यस्थिती रिअल-टाइममध्ये व्यवस्थापित करा.' 
                    : currentLanguage === 'hi' 
                    ? 'प्रत्येक ऑर्डर की वर्तमान स्थिति वास्तविक समय में प्रबंधित करें।' 
                    : 'Manage and observe the real-time preparation status of all active dining tables.'}
            </Typography>

            <Grid container spacing={4}>
                {/* Left Side: Active Orders List */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderBottom: '1px solid #e2e8f0' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#008751' }}>
                                {currentLanguage === 'mr' ? 'सक्रिय ऑर्डर्स' : currentLanguage === 'hi' ? 'सक्रिय ऑर्डर्स' : 'Active Orders'}
                            </Typography>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {orders.map((order) => {
                                const isSelected = order.id === selectedOrderId;
                                return (
                                    <ListItem 
                                        key={order.id} 
                                        button 
                                        onClick={() => setSelectedOrderId(order.id)}
                                        sx={{
                                            borderBottom: '1px solid #f1f5f9',
                                            bgcolor: isSelected ? '#e6f4ea' : 'transparent',
                                            '&:hover': { bgcolor: '#f0fdf4' }
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>{order.id}</Typography>
                                                    <Chip 
                                                        label={order.table} 
                                                        size="small" 
                                                        sx={{ bgcolor: '#008751', color: '#fff', fontWeight: 700 }} 
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <Box sx={{ mt: 0.5 }}>
                                                    <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                                                        {order.items}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#008751', fontWeight: 700 }}>
                                                        {steps[order.statusStep]}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Card>
                </Grid>

                {/* Right Side: Interactive Stepper Visualizer */}
                <Grid item xs={12} md={8}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3, boxShadow: 'none' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                    {selectedOrder.id} - {selectedOrder.table}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    {selectedOrder.items}
                                </Typography>
                            </Box>
                            <Chip 
                                label={steps[selectedOrder.statusStep]} 
                                color={selectedOrder.statusStep === 3 ? 'success' : 'warning'}
                                sx={{ fontWeight: 800 }}
                            />
                        </Box>

                        <Box sx={{ py: 6, px: 2, bgcolor: '#f8fafc', borderRadius: 3, mb: 4 }}>
                            <Stepper activeStep={selectedOrder.statusStep} alternativeLabel>
                                {steps.map((label, index) => (
                                    <Step key={label}>
                                        <StepLabel 
                                            StepIconComponent={() => (
                                                <Box sx={{ 
                                                    width: 40, 
                                                    height: 40, 
                                                    borderRadius: '50%', 
                                                    bgcolor: selectedOrder.statusStep >= index ? '#e6f4ea' : '#f1f5f9',
                                                    border: selectedOrder.statusStep >= index ? '2px solid #008751' : '2px solid #cbd5e1',
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    boxShadow: selectedOrder.statusStep === index ? '0 0 10px rgba(0, 135, 81, 0.4)' : 'none'
                                                }}>
                                                    {getStepIcon(index)}
                                                </Box>
                                            )}
                                        >
                                            <Typography sx={{ fontWeight: selectedOrder.statusStep === index ? 800 : 500, fontSize: '0.85rem' }}>
                                                {label}
                                            </Typography>
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button 
                                variant="outlined" 
                                disabled={selectedOrder.statusStep === 0}
                                onClick={() => handlePrevStep(selectedOrder.id)}
                                sx={{ fontWeight: 700 }}
                            >
                                {currentLanguage === 'mr' ? 'मागील स्थिती' : currentLanguage === 'hi' ? 'पिछली स्थिति' : 'Previous Status'}
                            </Button>
                            <Button 
                                variant="contained" 
                                disabled={selectedOrder.statusStep === 3}
                                onClick={() => handleNextStep(selectedOrder.id)}
                                sx={{ fontWeight: 700, bgcolor: '#008751', '&:hover': { bgcolor: '#006f42' } }}
                            >
                                {currentLanguage === 'mr' ? 'पुढील स्थिती' : currentLanguage === 'hi' ? 'अगली स्थिति' : 'Next Status'}
                            </Button>
                        </Box>
                    </Card>

                    <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                        {currentLanguage === 'mr' 
                            ? 'टीप: कॅप्टन ॲप किंवा KDS स्क्रीनवरून स्थिती बदलल्यास हा डॅशबोर्ड आपोआप अपडेट होतो.' 
                            : currentLanguage === 'hi' 
                            ? 'नोट: कैप्टन ऐप या KDS स्क्रीन से स्थिति बदलने पर यह डैशबोर्ड स्वचालित रूप से अपडेट हो जाता है।' 
                            : 'Status changes triggered from the Captain PWA or Kitchen KDS instantly sync with this live timeline.'}
                    </Alert>
                </Grid>
            </Grid>
        </Box>
    );
}
